import { GroupVsRemainder } from './types';
import styled from 'styled-components';
import Typography from '../../kingpin/atoms/Typography';
import React, { useContext, useMemo } from 'react';
import useValueFormatters from '../../hooks/useValueFormatters';
import toSentenceCase from '../../services/toSentenceCase';
import Icon from '../../kingpin/atoms/Icon';
import Row from '../Common/Row';
import formatDateLabel from '../V5Gadget/formatDateLabel';
import { ChartColors } from '../../theme/tokens';
import relativeDateRangeTypeCheckers from '../../contextProviders/SplashScreenProviders/UserAndAccountProviders/FiscalPeriodsProvider/relativeDateRangeTypeCheckers';
import BaseViewsContext from '../../contexts/BaseViewsContext';
import getSelectedSeriesName from './getSelectedSeriesName';

const TooltipDiv = styled.div`
  padding: 12px;
  border-radius: 2px;
  background: var(--color-bg-primary);
  box-shadow: var(--shadow-tooltip);
`;

const Datapoint = styled.div`
  display: flex;
  flex-direction: row;
  padding: 5px 10px;
  gap: 1px;
  border-radius: 6px;
  background-color: var(--color-bg-tertiary);
`;

const ColorSwatchContainer = styled.div`
  padding-top: 13px;
  padding-right: 8px;
`;
const ColorSwatchDiv = styled.div<{ color: string }>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  margin-top: 8px;
  margin-right: 4px;
  background-color: ${(props) => props.color};
`;

const useIsPositiveDeltaGood = (field: string, dataset: string) => {
  const { baseViews } = useContext(BaseViewsContext);

  const positiveDeltaIsGood = useMemo(() => {
    const baseView = baseViews[dataset];

    if (!baseView) {
      // throw error
      const e = new Error();
      e.name = 'BaseViewNotFound: Base view not found for dataset: ' + dataset;
      throw e;
    }
    const baseValue = baseView.fields[field];

    if (!baseValue) {
      const e = new Error();
      e.name = 'BaseValueNotFound: Base value not found for field: ' + field;
      throw e;
    }

    return !!baseValue.formatting.positiveDeltaIsGood;
  }, [baseViews, dataset, field]);

  return positiveDeltaIsGood;
};

const Difference = ({
  total,
  value,
  point,
}: {
  total: number;
  value: number;
  point: GroupVsRemainder.Point;
}) => {
  const { formatField } = useValueFormatters();
  const positiveDeltaIsGood = useIsPositiveDeltaGood(
    point.field,
    point.dataset,
  );
  const difference: {
    type: 'less' | 'greater' | 'equal';
    value: number;
    color: string;
    icon: Kingpin.Icon;
    percent?: string;
  } = useMemo(() => {
    if (total > value) {
      return {
        type: 'less',
        value: ((total - value) * 100) / 100,
        color: positiveDeltaIsGood
          ? 'var(--color-status-error)'
          : 'var(--color-success-v2)',
        icon: 'arrow-down',
        percent:
          value === 0 ? '' : `-${Math.round(100 - (value / total) * 100)}%`,
      };
    } else if (total < value) {
      return {
        type: 'greater',
        value: ((value - total) * 100) / 100,
        color: positiveDeltaIsGood
          ? 'var(--color-success-v2)'
          : 'var(--color-status-error)',
        icon: 'arrow-up',
        percent:
          total === 0 ? '' : `+${Math.round((value / total) * 100) - 100}%`,
      };
    } else {
      return {
        type: 'equal',
        value,
        color: 'var(--color-grey-3)',
        icon: 'minus',
      };
    }
  }, [positiveDeltaIsGood, total, value]);

  return (
    <Row
      id={'group-vs-remainder-tooltip-difference'}
      style={{ justifyContent: 'center', gap: 5 }}
    >
      <Icon
        color={difference.color}
        height={18}
        width={18}
        icon={difference.icon}
      />
      <span style={{ marginTop: -2 }}>
        <Typography.Body type={'Body 14 Bold'} color={difference.color}>
          {formatField({
            value: difference.value,
            field: point.field,
            dataset: point.dataset,
          })}{' '}
          {difference.percent && <span>({difference.percent})</span>}
        </Typography.Body>
      </span>
    </Row>
  );
};

const RenderPoint = ({
  groupValue,
  remainderValue,
  remainderSeriesName,
  point,
}: {
  groupValue: number | null | undefined;
  remainderValue: number | null | undefined;
  remainderSeriesName: string;
  point: GroupVsRemainder.Point;
}) => {
  const { formatField } = useValueFormatters();

  if (
    groupValue === null ||
    groupValue === undefined ||
    remainderValue === null ||
    remainderValue === undefined
  )
    return null;

  return (
    <div
      style={{
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
    >
      <Difference value={groupValue} point={point} total={remainderValue} />
      <div
        style={{
          display: 'flex',
          gap: 10,
          justifyContent: 'space-around',
        }}
      >
        <Datapoint>
          <ColorSwatchContainer>
            <ColorSwatchDiv color={ChartColors[0]} />
          </ColorSwatchContainer>
          <div>
            <Typography.Header type={'H4'}>
              {getSelectedSeriesName(point.selectedEntities)}
            </Typography.Header>
            <Typography.Body type={'Body 13 Bold'}>
              {formatField({
                value: groupValue,
                field: point.field,
                dataset: point.dataset,
              })}
            </Typography.Body>
          </div>
        </Datapoint>
        <Datapoint>
          <ColorSwatchContainer>
            <ColorSwatchDiv color={'orange'} />
          </ColorSwatchContainer>
          <div>
            <Typography.Header type={'H4'}>
              {remainderSeriesName}
            </Typography.Header>
            <Typography.Body type={'Body 13 Bold'}>
              {formatField({
                value: remainderValue,
                dataset: point.dataset,
                field: point.field,
              })}
            </Typography.Body>
          </div>
        </Datapoint>
      </div>
    </div>
  );
};

const MedianRenderPoint = ({ point }: { point: GroupVsRemainder.Point }) => {
  const { formatField } = useValueFormatters();

  return (
    <div
      style={{
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
    >
      <div style={{ margin: 'auto' }}>
        <Typography.Header type={'H3'}>
          {formatField({
            field: point.field,
            dataset: point.dataset,
            value: point.y,
          })}
        </Typography.Header>
      </div>
    </div>
  );
};

const GroupVsRemainderTooltip = ({
  points,
  defaultPoint,
}: {
  points: GroupVsRemainder.Point[];
  defaultPoint: GroupVsRemainder.Point;
}) => {
  const groupPoint = points.find((p) => p.dataType === 'group');

  const header = useMemo(() => {
    return defaultPoint.dataType === 'remainder'
      ? `Median ${toSentenceCase(defaultPoint.field)}`
      : `${getSelectedSeriesName(defaultPoint.selectedEntities)} ${toSentenceCase(defaultPoint.field)}`;
  }, [
    defaultPoint.dataType,
    defaultPoint.field,
    defaultPoint.selectedEntities,
  ]);

  return (
    <TooltipDiv style={{ margin: 'auto' }}>
      <div
        id={'group-vs-remainder-tooltip'}
        style={{ margin: 'auto', display: 'flex', justifyContent: 'center' }}
      >
        <Typography.Header type={'H3'} color={'var(--color-grey-3)'}>
          {header}
        </Typography.Header>
      </div>
      <div style={{ padding: 10 }}>
        {/* Only one series dataset, render simple tooltip */}
        {points.length === 1 && <MedianRenderPoint point={defaultPoint} />}
        {/* Two series datasets, render comparison tooltip */}
        {points.length > 1 && groupPoint && (
          <RenderPoint
            groupValue={groupPoint.y}
            point={groupPoint}
            remainderSeriesName={defaultPoint.series.name}
            remainderValue={defaultPoint.y}
          />
        )}
      </div>
      <div
        style={{ margin: 'auto', display: 'flex', justifyContent: 'center' }}
      >
        <Typography.Body type={'Body 13'}>
          <>
            {relativeDateRangeTypeCheckers.isFiscalInterval(
              defaultPoint.interval,
            ) && <div></div>}
            {relativeDateRangeTypeCheckers.isCalendarWithAutoInterval(
              defaultPoint.interval,
            ) && (
              <div>
                {formatDateLabel(
                  defaultPoint.startDate,
                  defaultPoint.interval,
                  true,
                )}
              </div>
            )}
          </>
        </Typography.Body>
      </div>
    </TooltipDiv>
  );
};

const Gate = ({ points }: { points: GroupVsRemainder.Point[] | undefined }) => {
  if (!points || points.length === 0) {
    return null;
  }

  const defaultPoint =
    points.length === 2
      ? points.find((p) => p.dataType === 'remainder')
      : points[0];

  if (!defaultPoint) return null;

  return (
    <GroupVsRemainderTooltip points={points} defaultPoint={defaultPoint} />
  );
};

export default Gate;
